import {html, type TemplateResult} from "lit";
import {customElement, property} from "lit/decorators.js";
import Styles from "./colorSchemeToggle.lit.scss";
import {ManagingResources} from "../../../../common/lifetime";
import {resolve} from "../../../../container";
import {EventBus, EventBusEvent} from "../../../../common/eventBus";
import {Dictionary} from "../../../elements/dictionary";
import {COLOR_SCHEME_CHANGED_EVENT, ColorScheme, type ColorSchemeData, ColorSchemeService} from "../../../../common/colorSchemeService";
import {UnLitElement} from "../../../../common/elements";
import {ClickTracking} from "../../../../tracking/clickTracking";
import {handleActivationKey} from "../../../../common/utils/events";

@customElement("eop-color-scheme-toggle")
export class EopColorSchemeToggle extends ManagingResources(UnLitElement) {

    public static readonly styles = Styles;

    @property({attribute: "color-scheme", reflect: true})
    public colorScheme = ColorScheme.LIGHT;

    private dictionary: Dictionary;

    public constructor(
        private eventBus: EventBus = resolve(EventBus),
        private colorSchemeService: ColorSchemeService = resolve(ColorSchemeService),
        private clickTracking: ClickTracking = resolve(ClickTracking)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);

        this.eventBus.on(COLOR_SCHEME_CHANGED_EVENT, (event: EventBusEvent<ColorSchemeData>) => {
            this.colorScheme = event.data.colorScheme;
        });
        this.colorScheme = this.colorSchemeService.getColorScheme();
    }

    public render(): TemplateResult {
        return html`
            <div class="color-scheme-toggle" tabindex="0" @click=${this.toggleColorScheme} @keydown=${this.handleKeydown}>
                <div class="color-scheme-label">${this.renderLabel()}</div>
            </div>
        `;
    }

    public focusableElement(): HTMLElement {
        return this.renderRoot.querySelector(".color-scheme-toggle")!;
    }

    private renderLabel(): TemplateResult {
        if (this.colorScheme === ColorScheme.LIGHT) {
            return html`${this.dictionary.translate("MSG_LIGHT_SCHEME_TOGGLE_LABEL")}`;
        } else {
            return html`${this.dictionary.translate("MSG_DARK_SCHEME_TOGGLE_LABEL")}`;
        }
    }

    private handleKeydown(e: KeyboardEvent): void {
        handleActivationKey(e, () => this.toggleColorScheme(e));
    }

    private toggleColorScheme(event: Event): void {
        const colorModeTransition = this.colorScheme === ColorScheme.LIGHT ? "light->dark" : "dark->light";
        event.stopPropagation();
        this.clickTracking.handleNavigationInteract(event, "color-scheme-toggle: " + colorModeTransition);

        this.colorSchemeService.toggleColorScheme();
    }
}